import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTemplateStore } from "@/stores/template.store";
import { CircleCheck, CircleX, CopyIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function DetailedReport() {
  const { report, setReport } = useTemplateStore();
  const [open, setOpen] = useState(false);

  if (!report) return null;
  const reportData = report?.countData;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">View Email Report</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl ">
        <DialogHeader>
          <DialogTitle>Email Template Report</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[80vh] pr-4">
          <Accordion
            type="single"
            defaultValue="spam-words"
            collapsible
            className="w-full"
          >
            <AccordionItem value="spam-words">
              <AccordionTrigger>Spam Words</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground mb-2">
                  {reportData?.spamWords.count} spam words detected
                </p>
                {reportData?.spamWords.count > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Count</TableHead>
                        <TableHead>Words</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(reportData?.spamWords.details || {})
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([category, data]) => (
                          <TableRow key={category}>
                            <TableCell className="capitalize">
                              {category}
                            </TableCell>
                            <TableCell>{data.count}</TableCell>
                            <TableCell>{data.words.join(", ")}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="images">
              <AccordionTrigger>Images</AccordionTrigger>
              <AccordionContent>
                <div className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <span>Total images: {reportData?.image.imageCount}</span>
                  <Separator orientation="vertical" className="h-4" />
                  <span>Invalid: {reportData?.image.invalidCount},</span>
                  <Separator orientation="vertical" className="h-4" />
                  <span>Empty alt: {reportData?.image.emptyAltCount}</span>
                </div>
                {reportData?.image.details.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source</TableHead>
                        <TableHead>Alt</TableHead>
                        <TableHead>Valid</TableHead>
                        <TableHead>Issues</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData?.image.details.map((img, index) => (
                        <TableRow key={index}>
                          <TableCell className="max-w-[200px]">
                            <div className="flex items-center gap-2">
                              <div className="truncate flex-1">
                                <span className="truncate ">{img.src}</span>
                              </div>
                              <CopyButton text={img.src} />
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            {img.alt}
                          </TableCell>
                          <TableCell>
                            {img.isValid ? (
                              <Badge
                                variant="success"
                                className="text-xs shadow-none"
                              >
                                Valid
                              </Badge>
                            ) : (
                              <Badge
                                variant="destructive"
                                className="text-xs shadow-none"
                              >
                                Invalid
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {img.issues.map((issue) => (
                              <span
                                key={issue}
                                className="text-xs bg-secondary rounded-lg px-3 py-1 "
                              >
                                {issue}
                              </span>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="broken-links">
              <AccordionTrigger>Links</AccordionTrigger>
              <AccordionContent>
                <div className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <span>Total Links: {reportData?.links.count}</span>
                  <Separator orientation="vertical" className="h-4" />
                  <span>
                    Valid Links:{" "}
                    {
                      reportData?.links.details.filter((link) => link.isValid)
                        .length
                    }{" "}
                  </span>
                  <Separator orientation="vertical" className="h-4" />
                  <span>
                    Invalid Links:{" "}
                    {
                      reportData?.links.details.filter((link) => !link.isValid)
                        .length
                    }{" "}
                  </span>
                </div>
                {reportData?.links.count > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Link</TableHead>
                        <TableHead>Text</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData?.links.details.map((link, index) => (
                        <TableRow key={index}>
                          <TableCell className="max-w-[200px]">
                            <div className="flex items-center gap-2">
                              <div className=" truncate">
                                <span className="truncate flex-1">
                                  {link.href}
                                </span>
                              </div>
                              <CopyButton text={link.href} />
                            </div>
                          </TableCell>
                          <TableCell>{link.text}</TableCell>
                          <TableCell>
                            {link.isValid ? (
                              <Badge
                                variant="success"
                                className="text-xs shadow-none"
                              >
                                Valid
                              </Badge>
                            ) : (
                              <Badge
                                variant="destructive"
                                className="text-xs shadow-none"
                              >
                                Invalid
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {link.reason && (
                              <Badge
                                key={link.reason}
                                variant="secondary"
                                className="text-xs shadow-none"
                              >
                                {link.reason}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="dangerous-html">
              <AccordionTrigger>Dangerous HTML</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground mb-2">
                  {reportData?.dangerousHtml.count} dangerous HTML elements
                  detected
                </p>
                {reportData?.dangerousHtml.count > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData?.dangerousHtml.details.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.type}</TableCell>
                          <TableCell>{item.src}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="shortened-urls">
              <AccordionTrigger>Shortened URLs</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground mb-2">
                  {reportData?.shortenedUrls.count} shortened URLs detected
                </p>
                {reportData?.shortenedUrls.count > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>URL</TableHead>
                        <TableHead>Service</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData?.shortenedUrls.details.map((url, index) => (
                        <TableRow key={index}>
                          <TableCell>{url.url}</TableCell>
                          <TableCell>{url.service}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="questions">
              <AccordionTrigger>Questions</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground mb-2">
                  {reportData?.questions.length} questions detected
                </p>
                {reportData?.questions.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Question</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData?.questions.map((question, index) => (
                        <TableRow key={index}>
                          <TableCell>{question}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="personalisation">
              <AccordionTrigger>Personalization</AccordionTrigger>
              <AccordionContent>
                <div className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <span>
                    Total personalizations:{" "}
                    {reportData?.personalisation?.allTokensCount}
                  </span>
                  <Separator orientation="vertical" className="h-4" />
                  <span>
                    Invalid personalizations:{" "}
                    {reportData?.personalisation?.invalidCount}
                  </span>
                </div>
                {reportData?.personalisation?.details.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Word</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData?.personalisation?.details.map(
                        (token, index) => (
                          <TableRow key={index}>
                            <TableCell>{token.token}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  token.isValid ? "success" : "destructive"
                                }
                                className="text-xs shadow-none"
                              >
                                {token.isValid ? "Valid" : "Invalid"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="unsubscribe-link">
              <AccordionTrigger>Unsubscribe Link</AccordionTrigger>
              <AccordionContent>
                <div>
                  {reportData?.unsubscribeLink.have ? (
                    <div className="flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-green-600" />
                      <span>Have an unsubscribe link</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CircleX className="w-4 h-4 text-red-600" />
                      <span>No unsubscribe link</span>
                    </div>
                  )}
                  {reportData?.unsubscribeLink.have && (
                    <div className="max-w-[200px]">
                      <div className="flex items-center gap-2">
                        <div className=" truncate">
                          <span className="truncate flex-1">
                            {reportData?.unsubscribeLink.link}
                          </span>
                        </div>
                        <CopyButton text={reportData?.unsubscribeLink.link} />
                      </div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="read-time">
              <AccordionTrigger>Read Time</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground">
                  Estimated read time:{" "}
                  {Math.floor(reportData?.readTime.seconds / 60)} minutes{" "}
                  {reportData?.readTime.seconds % 60} seconds
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

const CopyButton = ({ text }: { text: string }) => {
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };
  return (
    <Button variant="ghost" size="icon" onClick={() => handleCopy(text)}>
      <CopyIcon className="w-4 h-4" />
    </Button>
  );
};
